import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowRight, Building2, Users, Mail, RefreshCw } from 'lucide-react';

export default function ImportProspects() {
    const [mode, setMode] = useState('new');
    return (
        <div data-testid="import-prospects-page" className="bm-fade-in">
            <PageHeader
                title="Import Prospects"
                description={mode === 'new'
                    ? 'Upload a Companies + Contacts workbook to add prospects into your CRM — with UPSERT logic so re-imports update rather than duplicate.'
                    : 'Upload a corrected Contacts workbook (e.g. bounce fixes) — matches by contact_import_id only and updates existing contacts, never creates new ones.'}
            />
            <div className="px-8 pt-5 flex items-center gap-2">
                <button onClick={() => setMode('new')} data-testid="import-mode-new-btn"
                        className={`text-[12.5px] font-medium px-3 py-1.5 rounded-full border transition ${mode === 'new' ? 'bg-[color:var(--bm-primary)] text-white border-transparent' : 'border-[color:var(--bm-border)] text-[color:var(--bm-text-muted)] hover:bg-[color:var(--bm-bg-mute)]'}`}>
                    Add / update prospects
                </button>
                <button onClick={() => setMode('update')} data-testid="import-mode-update-btn"
                        className={`text-[12.5px] font-medium px-3 py-1.5 rounded-full border transition ${mode === 'update' ? 'bg-[color:var(--bm-primary)] text-white border-transparent' : 'border-[color:var(--bm-border)] text-[color:var(--bm-text-muted)] hover:bg-[color:var(--bm-bg-mute)]'}`}>
                    Update existing contacts (bounce corrections)
                </button>
            </div>
            {mode === 'new' ? <NewProspectsImport /> : <ContactUpdateImport />}
        </div>
    );
}

function NewProspectsImport() {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [result, setResult] = useState(null);

    const onFile = (f) => {
        setFile(f);
        setPreview(null);
        setResult(null);
    };

    const upload = async () => {
        if (!file) return toast.error('Choose a file first');
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/leadgen/import/prospects/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setPreview(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setUploading(false); }
    };

    const commit = async () => {
        if (!preview) return;
        setCommitting(true);
        try {
            const r = await api.post(`/leadgen/import/prospects/${preview.import_id}/commit`);
            setResult(r.data);
            toast.success('Import complete');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setCommitting(false); }
    };

    return (
            <div className="px-8 py-6 max-w-3xl">
                {!result && (
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="import-upload-card">
                        <div className="flex items-center gap-3">
                            <label className="flex-1 flex items-center gap-3 border border-dashed border-[color:var(--bm-border)] rounded-lg px-4 py-6 cursor-pointer hover:bg-[color:var(--bm-bg-mute)] transition"
                                   data-testid="import-file-label">
                                <FileSpreadsheet size={28} className="text-[color:var(--bm-text-subtle)] shrink-0" />
                                <div>
                                    <div className="text-[13px] font-medium">{file ? file.name : 'Choose an .xlsx or .csv file'}</div>
                                    <div className="text-[11.5px] text-[color:var(--bm-text-subtle)] mt-0.5">Companies + Contacts sheets are detected automatically.</div>
                                </div>
                                <input type="file" accept=".xlsx,.xls,.csv" className="hidden"
                                       onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
                            </label>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <Button onClick={upload} disabled={!file || uploading} style={{ background: 'var(--bm-primary)' }} data-testid="import-preview-btn">
                                <Upload size={14} className="mr-1.5" /> {uploading ? 'Reading…' : 'Preview import'}
                            </Button>
                        </div>
                    </div>
                )}

                {preview && !result && (
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 mt-5" data-testid="import-preview-card">
                        <div className="font-display text-[16px] font-semibold mb-4">Preview — nothing has been saved yet</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <PreviewStat label="Companies found" value={preview.companies_found} />
                            <PreviewStat label="New companies" value={preview.companies_new} accent="emerald" />
                            <PreviewStat label="Companies updated" value={preview.companies_existing} accent="amber" />
                            <PreviewStat label="Contacts found" value={preview.contacts_found} />
                            <PreviewStat label="New contacts" value={preview.contacts_new} accent="emerald" />
                            <PreviewStat label="Duplicate contacts" value={preview.contacts_duplicate} accent="amber" />
                            <PreviewStat label="Verified emails" value={preview.verified_emails} accent="emerald" />
                            <PreviewStat label="Inferred emails" value={preview.inferred_emails} accent="amber" />
                        </div>
                        {preview.errors?.length > 0 && (
                            <div className="mt-4 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="import-errors">
                                <div className="flex items-center gap-1.5 font-semibold mb-1"><AlertTriangle size={13} /> {preview.errors.length} row(s) skipped</div>
                                <ul className="list-disc ml-4 space-y-0.5">
                                    {preview.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        )}
                        <div className="mt-5 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }} data-testid="import-cancel-btn">Cancel</Button>
                            <Button onClick={commit} disabled={committing} style={{ background: 'var(--bm-primary)' }} data-testid="import-confirm-btn">
                                {committing ? 'Importing…' : 'Confirm import'} <ArrowRight size={14} className="ml-1.5" />
                            </Button>
                        </div>
                    </div>
                )}

                {result && (
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 mt-5 text-center" data-testid="import-result-card">
                        <CheckCircle2 size={36} className="mx-auto text-emerald-500" />
                        <div className="font-display text-[18px] font-semibold mt-3">
                            {result.companies_created + result.companies_updated} Companies and {result.contacts_created + result.contacts_updated} Contacts successfully imported
                        </div>
                        <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-1.5">
                            {result.companies_created} new · {result.companies_updated} updated companies &nbsp;·&nbsp; {result.contacts_created} new · {result.contacts_updated} updated contacts
                            {result.contacts_skipped > 0 && <> · {result.contacts_skipped} skipped (no matching company)</>}
                        </div>
                        <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
                            <Button variant="outline" onClick={() => navigate('/clients?status=lead')} data-testid="import-view-companies-btn">
                                <Building2 size={14} className="mr-1.5" /> View Companies
                            </Button>
                            <Button variant="outline" onClick={() => navigate('/clients?status=lead')} data-testid="import-view-contacts-btn">
                                <Users size={14} className="mr-1.5" /> View Contacts
                            </Button>
                            <Button onClick={() => navigate('/leadgen/email-campaigns/new')} style={{ background: 'var(--bm-primary)' }} data-testid="import-create-campaign-btn">
                                <Mail size={14} className="mr-1.5" /> Create Email Campaign
                            </Button>
                        </div>
                        <button className="mt-5 text-[12.5px] text-[color:var(--bm-text-muted)] underline" onClick={() => { setResult(null); setPreview(null); setFile(null); }} data-testid="import-another-btn">
                            Import another file
                        </button>
                    </div>
                )}
            </div>
    );
}

function ContactUpdateImport() {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [result, setResult] = useState(null);

    const onFile = (f) => {
        setFile(f);
        setPreview(null);
        setResult(null);
    };

    const upload = async () => {
        if (!file) return toast.error('Choose a file first');
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/leadgen/import/contacts-update/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setPreview(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setUploading(false); }
    };

    const commit = async () => {
        if (!preview) return;
        setCommitting(true);
        try {
            const r = await api.post(`/leadgen/import/contacts-update/${preview.import_id}/commit`);
            setResult(r.data);
            toast.success('Contacts updated');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setCommitting(false); }
    };

    return (
        <div className="px-8 py-6 max-w-3xl">
            {!result && (
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="contact-update-upload-card">
                    <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mb-4 flex items-start gap-2 bg-[color:var(--bm-bg-mute)] rounded-lg p-3">
                        <RefreshCw size={14} className="mt-0.5 shrink-0" />
                        <span>Matches each row by <strong>contact_import_id</strong> only. Existing contacts get their email, email status, confidence, evidence and contact status overwritten — and are reset to <strong>Not Contacted</strong> so they're ready for a new campaign. No new contacts or companies are ever created here.</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="flex-1 flex items-center gap-3 border border-dashed border-[color:var(--bm-border)] rounded-lg px-4 py-6 cursor-pointer hover:bg-[color:var(--bm-bg-mute)] transition"
                               data-testid="contact-update-file-label">
                            <FileSpreadsheet size={28} className="text-[color:var(--bm-text-subtle)] shrink-0" />
                            <div>
                                <div className="text-[13px] font-medium">{file ? file.name : 'Choose an .xlsx or .csv file'}</div>
                                <div className="text-[11.5px] text-[color:var(--bm-text-subtle)] mt-0.5">A 'Contacts' sheet with a contact_import_id column is required.</div>
                            </div>
                            <input type="file" accept=".xlsx,.xls,.csv" className="hidden"
                                   onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid="contact-update-file-input" />
                        </label>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <Button onClick={upload} disabled={!file || uploading} style={{ background: 'var(--bm-primary)' }} data-testid="contact-update-preview-btn">
                            <Upload size={14} className="mr-1.5" /> {uploading ? 'Reading…' : 'Preview update'}
                        </Button>
                    </div>
                </div>
            )}

            {preview && !result && (
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 mt-5" data-testid="contact-update-preview-card">
                    <div className="font-display text-[16px] font-semibold mb-4">Preview — nothing has been saved yet</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <PreviewStat label="Rows found" value={preview.contacts_found} />
                        <PreviewStat label="Will update" value={preview.will_update} accent="emerald" />
                        <PreviewStat label="No matching contact" value={preview.not_found} accent="amber" />
                        {preview.missing_contact_import_id > 0 && (
                            <PreviewStat label="Missing contact_import_id" value={preview.missing_contact_import_id} accent="amber" />
                        )}
                    </div>
                    {preview.errors?.length > 0 && (
                        <div className="mt-4 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="contact-update-errors">
                            <div className="flex items-center gap-1.5 font-semibold mb-1"><AlertTriangle size={13} /> {preview.errors.length} row(s) skipped</div>
                            <ul className="list-disc ml-4 space-y-0.5">
                                {preview.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        </div>
                    )}
                    <div className="mt-5 flex justify-end gap-2">
                        <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }} data-testid="contact-update-cancel-btn">Cancel</Button>
                        <Button onClick={commit} disabled={committing || preview.will_update === 0} style={{ background: 'var(--bm-primary)' }} data-testid="contact-update-confirm-btn">
                            {committing ? 'Updating…' : `Update ${preview.will_update} contact(s)`} <ArrowRight size={14} className="ml-1.5" />
                        </Button>
                    </div>
                </div>
            )}

            {result && (
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 mt-5 text-center" data-testid="contact-update-result-card">
                    <CheckCircle2 size={36} className="mx-auto text-emerald-500" />
                    <div className="font-display text-[18px] font-semibold mt-3">
                        {result.contacts_updated} contact(s) corrected and reset to Not Contacted
                    </div>
                    <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-1.5">
                        {result.not_found > 0 && <>{result.not_found} row(s) had no matching contact_import_id — no new contacts were created</>}
                        {result.skipped_missing_id > 0 && <> · {result.skipped_missing_id} row(s) skipped (no contact_import_id in the sheet)</>}
                    </div>
                    {result.not_found_rows?.length > 0 && (
                        <div className="mt-4 text-left text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="contact-update-not-found">
                            <div className="font-semibold mb-1">Not found:</div>
                            <ul className="list-disc ml-4 space-y-0.5">
                                {result.not_found_rows.map((r, i) => <li key={i}>{r.contact_import_id} — {r.name || r.email}</li>)}
                            </ul>
                        </div>
                    )}
                    <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
                        <Button variant="outline" onClick={() => navigate('/leadgen/inbox')} data-testid="contact-update-view-inbox-btn">
                            <Users size={14} className="mr-1.5" /> View Contacts
                        </Button>
                        <Button onClick={() => navigate('/leadgen/email-campaigns/new')} style={{ background: 'var(--bm-primary)' }} data-testid="contact-update-create-campaign-btn">
                            <Mail size={14} className="mr-1.5" /> Create Email Campaign
                        </Button>
                    </div>
                    <button className="mt-5 text-[12.5px] text-[color:var(--bm-text-muted)] underline" onClick={() => { setResult(null); setPreview(null); setFile(null); }} data-testid="contact-update-another-btn">
                        Update another file
                    </button>
                </div>
            )}
        </div>
    );
}

function PreviewStat({ label, value, accent }) {
    const toneMap = { emerald: 'text-emerald-700', amber: 'text-amber-700' };
    return (
        <div className="rounded-lg border border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] p-3">
            <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{label}</div>
            <div className={`mt-1 font-display text-[18px] font-semibold ${toneMap[accent] || ''}`}>{value}</div>
        </div>
    );
}
